import type { TenantWelcomeTranslation } from '@/features/tenant/tenant-translation-section'
import type { ITenant, UpdateTenantPayload } from '@/shared/api/types/tenant-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { z } from 'zod'
import {
  useTenantLanguages,
  useUpdateTenantInfoMutation,
  useUpdateTenantMutation,
} from '@/entities/tenant'
import { TenantTranslationSection } from '@/features/tenant/tenant-translation-section'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { optionalPhoneSchema } from '@/shared/libs/phone-validation'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { resolveDefaultLanguages } from '@/shared/utils/language-utils'

const tenantFormSchema = z.object({
  name: z.string().trim().min(1, 'common.required').max(100, 'tenant.validation.nameMaxLength'),
  slug: z.string().trim().min(1, 'common.required'),
  description: z.string().optional(),
  contactEmail: z.string().trim().min(1, 'common.required').email('common.invalidEmail'),
  phone: optionalPhoneSchema,
  website: z.string().trim().url('common.invalidUrl').or(z.literal('')).optional(),
  isActive: z.boolean(),
})

type TenantFormValues = z.infer<typeof tenantFormSchema>

function mapTenantLocalizedDataToTranslations(
  tenant: ITenant,
  languages: string[],
): TenantWelcomeTranslation[] {
  return languages.map((language) => {
    const localized = tenant.localizedData?.[language]
    return {
      language,
      name: localized?.['welcome_title'] ?? '',
      description: localized?.['welcome_description'] ?? '',
    }
  })
}

function buildTenantWelcomeSnapshot(translations: TenantWelcomeTranslation[]): string {
  return JSON.stringify(
    translations.map((item) => ({
      language: item.language,
      name: item.name.trim(),
      description: item.description.trim(),
    })),
  )
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

interface TenantGeneralInfoProps {
  tenant: ITenant
  onDirtyChange?: (isDirty: boolean) => void
}

export function TenantGeneralInfo({ tenant, onDirtyChange }: TenantGeneralInfoProps) {
  const { t } = useTranslation()
  const { isSuperAdmin } = useAdminScope()
  const canEditTenantBaseFields = isSuperAdmin
  const updateMutation = useUpdateTenantMutation()
  const tenantLanguagesQuery = useTenantLanguages(tenant.id)
  const resolvedLanguages = useMemo(() => {
    const activeLanguages = resolveDefaultLanguages(tenantLanguagesQuery.items)
    if (activeLanguages.length > 0) {
      return activeLanguages
    }
    return [tenant.defaultLanguage?.code ?? 'en']
  }, [tenant.defaultLanguage, tenantLanguagesQuery.items])
  const [translations, setTranslations] = useState<TenantWelcomeTranslation[]>([])
  const [hasPendingTranslationGeneration, setHasPendingTranslationGeneration] = useState(false)
  const [initialWelcomeSnapshot, setInitialWelcomeSnapshot] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: tenant.name,
      slug: tenant.slug,
      description: tenant.description,
      contactEmail: tenant.contactEmail,
      website: tenant.website ?? '',
      isActive: tenant.isActive,
      phone: tenant.phone ?? '',
    },
  })

  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined
  const isWelcomeDirty =
    buildTenantWelcomeSnapshot(translations) !== initialWelcomeSnapshot ||
    hasPendingTranslationGeneration

  useEffect(() => {
    const nextTranslations = mapTenantLocalizedDataToTranslations(tenant, resolvedLanguages)
    reset({
      name: tenant.name,
      slug: tenant.slug,
      description: tenant.description,
      contactEmail: tenant.contactEmail,
      website: tenant.website ?? '',
      isActive: tenant.isActive,
      phone: tenant.phone ?? '',
    })
    /* eslint-disable react-hooks/set-state-in-effect */
    setTranslations(nextTranslations)
    setInitialWelcomeSnapshot(buildTenantWelcomeSnapshot(nextTranslations))
    setHasPendingTranslationGeneration(false)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [tenant, reset, resolvedLanguages])

  useEffect(() => {
    onDirtyChange?.(isDirty || isWelcomeDirty)
  }, [isDirty, isWelcomeDirty, onDirtyChange])

  const updateInfoMutation = useUpdateTenantInfoMutation()

  async function onSubmitBasicInfo(values: TenantFormValues) {
    try {
      const payload: UpdateTenantPayload = {
        name: values.name,
        slug: values.slug,
        description: values.description ?? '',
        contactEmail: values.contactEmail,
        website: values.website ?? '',
        isActive: values.isActive,
        phone: values.phone ?? '',
      }
      await updateMutation.mutateAsync({ id: tenant.id, payload })
      reset(values)
      showToast.success(t('tenant.updateSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function onSubmitLocalizedInfo() {
    try {
      const localizedData = Object.fromEntries(
        translations.map((item) => [
          item.language,
          {
            ...(tenant.localizedData?.[item.language] ?? {}),
            welcome_title: item.name.trim(),
            welcome_description: item.description.trim(),
          },
        ]),
      )
      await updateInfoMutation.mutateAsync({ id: tenant.id, payload: { localizedData } })
      setInitialWelcomeSnapshot(buildTenantWelcomeSnapshot(translations))
      showToast.success(t('tenant.updateSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <div className="space-y-6">
      <form
        id="tenant-general-info"
        onSubmit={handleSubmit(onSubmitBasicInfo)}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <FormField label={t('tenant.name')} required error={getErrorText(errors.name?.message)}>
            <Input {...register('name')} />
          </FormField>

          <FormField label={t('tenant.slug')} required error={getErrorText(errors.slug?.message)}>
            <Input
              {...register('slug')}
              disabled={!canEditTenantBaseFields}
              onChange={(e) => {
                setValue('slug', toSlug(e.target.value), {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
            />
          </FormField>

          <FormField
            label={t('tenant.contactEmail')}
            required
            error={getErrorText(errors.contactEmail?.message)}
          >
            <Input inputMode="email" autoComplete="email" {...register('contactEmail')} />
          </FormField>

          <FormField label={t('tenant.phone')} error={getErrorText(errors.phone?.message)}>
            <Input type="tel" {...register('phone')} />
          </FormField>

          <FormField label={t('tenant.website')} error={getErrorText(errors.website?.message)}>
            <Input {...register('website')} />
          </FormField>

          <FormField
            label={t('common.description')}
            error={getErrorText(errors.description?.message)}
          >
            <Textarea rows={3} {...register('description')} className="resize-none" />
          </FormField>

          <div className="flex flex-col gap-2">
            <Label>{t('tenant.createdAt')}</Label>
            <div className="text-sm text-zinc-500 mt-1">
              {new Date(tenant.createdAt).toLocaleString()}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? t('common.saving') : t('common.update')}
          </Button>
        </div>
      </form>

      {tenant.defaultLanguage && (
        <p className="text-xs text-zinc-500">
          {t('tenant.language.default')}:{' '}
          {tenant.defaultLanguage.nativeName || tenant.defaultLanguage.name} (
          {tenant.defaultLanguage.code})
        </p>
      )}

      <TenantTranslationSection
        languages={resolvedLanguages}
        translations={translations}
        onChange={setTranslations}
        onGenerationStateChange={setHasPendingTranslationGeneration}
        entityId={tenant.id}
        entityLabel={tenant.name}
      />

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={updateInfoMutation.isPending}
          onClick={() => {
            void onSubmitLocalizedInfo()
          }}
        >
          {updateInfoMutation.isPending
            ? t('common.saving')
            : t('tenant.updateLocalized', { defaultValue: 'Cập nhật đa ngôn ngữ' })}
        </Button>
      </div>
    </div>
  )
}
